import React, { FC } from 'react';

import Multiclamp from 'react-multi-clamp';

import {
  LevelCompleteVertical,
  LevelIntermediateVertical,
  LevelEsencialVertical,
  User,
  Star,
} from '@crehana/icons';

import {
  LazyImageResponsive,
  colors,
  InverseButton,
  PrimaryButton,
} from '@crehana/ui';

import { getImgxUrl } from '@crehana/utils';

import { useTranslation } from '@crehana/i18n';

import { useMediaQuery } from '@crehana/react-hooks';

import unescapeChars from 'Shared/StringParser/unescapeChars';

import getCountryPrefix from 'Utils/getCountryPrefix';

import { HorizontalCardContainer, ImageWrapper } from './HorizontalCard.styles';
import formatDecimals from './helpers/formatDecimals';
import { dateOptions } from './constants';
import es from './i18n/es';
import pt from './i18n/pt';
import { HorizontalCardProps } from './types';

const { yellowMain, grayDark, baseLighter } = colors;
const countryPrefix = getCountryPrefix();

const HorizontalCard: FC<HorizontalCardProps> = ({
  course,
  languageKey = 'es',
  shouldGoToCourseDetail,
  className,
  onClickCTA,
  enrollToCourse,
}) => {
  const { matches: isDesktop } = useMediaQuery('sm');
  const { t } = useTranslation();
  const {
    title,
    imagePlaceholder,
    promoImage,
    description,
    score: { average, total },
    enrollments,
    url,
    level: { name },
    dateSchedule,
  } = course;
  const languageKeys = languageKey === 'pt' ? pt : es;

  const goToCourseDetail = () => {
    window.location.href = url;
  };

  return (
    <HorizontalCardContainer
      className={`flex sm:px-16 py-16 sm:py-24 border-b border-solid border-gray-light w-full ${
        shouldGoToCourseDetail ? 'cursor-pointer' : ''
      } ${className}`}
      onClick={() => {
        if (shouldGoToCourseDetail) {
          goToCourseDetail();
        }
      }}
    >
      <ImageWrapper href={url}>
        <LazyImageResponsive
          sources={{
            default: getImgxUrl(promoImage, { w: 96, h: 96, fit: 'fill' }),
            sm: getImgxUrl(promoImage, {
              w: 205,
              h: 140,
              fit: 'fill',
            }),
            md: getImgxUrl(promoImage, {
              w: 205,
              h: 140,
              fit: 'fill',
            }),
            lg: getImgxUrl(promoImage, {
              w: 205,
              h: 140,
              fit: 'fill',
            }),
          }}
          placeholder={imagePlaceholder}
          imgxParams={{
            fit: 'fill',
          }}
          style={{ height: '100%' }}
          alt="title"
          className="h-full lazyImg"
          imgClassName="object-cover w-full h-auto"
        />
      </ImageWrapper>
      <div className="flex flex-col items-start justify-center w-full pl-12 sm:pl-16">
        {dateSchedule && (
          <p className="font-subtitle3  text-purple-main mb-4 sm:mb-12 capitalize">
            {new Date(dateSchedule).toLocaleDateString(
              countryPrefix === 'br' ? 'pt-BR' : 'es-ES',
              // @ts-expect-error
              dateOptions,
            )}
          </p>
        )}
        <a href={url}>
          <h4 className="font-subtitle2 text-base-main mb-12 w-full hidden sm:flex cursor-pointer">
            <Multiclamp ellipsis="..." clamp={1}>
              {title}
            </Multiclamp>
          </h4>
          <h4 className="font-subtitle4 text-base-main mb-12 w-full flex sm:hidden">
            <Multiclamp ellipsis="..." clamp={2}>
              {title}
            </Multiclamp>
          </h4>
        </a>
        <div className="flex items-center mb-8 sm:mb-12">
          <div className="hidden sm:flex items-center mr-8 sm:mr-16">
            <Star color={yellowMain} size={24} className="mr-8" />
            <p className="font-subtitle3 mr-8">
              {formatDecimals({ number: average, decimals: 2 })}
            </p>
            {total > 0 && <p color="font-body3 gray-dark">({total})</p>}
          </div>
          <div className="flex sm:hidden items-center mr-8 sm:mr-16">
            <Star color={yellowMain} size={16} className="mr-8" />
            <p className="font-subtitle4 mr-8">
              {formatDecimals({ number: average, decimals: 2 })}
            </p>
            {total > 0 && <p color="font-body4 gray-dark">({total})</p>}
          </div>
          <div className="sm:flex items-center mr-16 hidden">
            <User size={22} className="mr-8" color={baseLighter} />
            <p className="font-subtitle3">
              {enrollments} {languageKeys.STUDENTS}
            </p>
          </div>
          <div className="flex items-center">
            {
              {
                Esencial: <LevelEsencialVertical size={18} color={grayDark} />,
                Intermedio: (
                  <LevelIntermediateVertical size={18} color={grayDark} />
                ),
                Avanzado: <LevelCompleteVertical size={18} color={grayDark} />,
                Completo: <LevelCompleteVertical size={18} color={grayDark} />,
              }[name]
            }
            <p className="font-subtitle3 ml-8 hidden sm:flex">Curso {name}</p>
            <p className="font-subtitle4 ml-8 flex sm:hidden">{name}</p>
          </div>
        </div>
        <div className="flex items-center justify-between w-full">
          <div className="font-body4 text-gray-dark w-full sm:w-4/6 hidden sm:flex">
            <Multiclamp ellipsis="..." clamp={3}>
              {unescapeChars(description)}
            </Multiclamp>
          </div>
          <div className="w-full sm:w-2/6 sm:pl-16 lg:w-1/6 lg:pl-0">
            {course.isSubscribe ? (
              <InverseButton
                full
                label={
                  course.courseType === 'temporal-access'
                    ? t('COURSE_AGENDA_ALREADY_ENROLLED')
                    : t('COURSE_AGENDA_ENROLL')
                }
                size={isDesktop ? 'm' : 's'}
                onClick={() => {
                  if (course.courseType === 'temporal-access') {
                    enrollToCourse();
                  } else {
                    window.location.href = course.url;
                  }
                }}
              />
            ) : (
              <PrimaryButton
                full
                size={isDesktop ? 'm' : 's'}
                label={t('COURSE_AGENDA_ENROLL')}
                onClick={onClickCTA}
              />
            )}
          </div>
        </div>
      </div>
    </HorizontalCardContainer>
  );
};

export default HorizontalCard;
